import { useMutation } from '@tanstack/vue-query'
import queryClient, { queryKeys } from '~/queryClient'
import { fetchCommissioningResetCustomerRejection } from '~/services-v2/financial-bff/commissioning-reset-customer-rejection.gql'

export class CommissioningResetCustomerRejectionError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'CommissioningResetCustomerRejectionError'
    this.code = code
  }
}

export default function useCommissioningResetCustomerRejection(projectId: Ref<string | undefined>) {
  const mutationKey = computed(() =>
    queryKeys.INSTALLATION.COMMISSIONING_RESET_CUSTOMER_REJECTION(projectId.value as string),
  )

  const mutationFn = async () => {
    if (!projectId.value) {
      throw new Error('Project ID is required')
    }

    const result = await fetchCommissioningResetCustomerRejection({
      project_id: projectId.value,
    })

    if (!result.success) {
      throw new CommissioningResetCustomerRejectionError(
        result.data?.code ?? 'TECHNICAL_FAILURE',
        result.message,
      )
    }

    return result
  }

  return useMutation({
    mutationKey,
    mutationFn,
    retry: 0,
    onSuccess: (result) => {
      const installationId = result.data.installation_id
      if (installationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.INSTALLATION.COMMISSIONING_STATUS(installationId),
        })
      }

      if (projectId.value) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.INSTALLATION.DETAILS(projectId),
        })
      }
    },
  })
}
